import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './FAQs.css';

const FAQ_DATA = [
  {
    question: 'What is a Lasting Power of Attorney (LPA) in England and Wales?',
    answer:
      'A Lasting Power of Attorney is a legally binding document under the Mental Capacity Act 2005 that allows you (the "donor") to appoint trusted individuals ("attorneys") to make decisions on your behalf if you lose mental capacity. An LPA must be registered with the Office of the Public Guardian before it can be used. There are two types: a Property and Financial Affairs LPA and a Health and Welfare LPA. Most clients choose to put both in place.',
  },
  {
    question: 'What is the difference between a Lasting Power of Attorney and a Will?',
    answer:
      'A Will takes effect after death and deals with the distribution of your estate. A Lasting Power of Attorney applies during your lifetime and is used if you lose mental capacity. They serve different purposes and both are essential components of estate planning.',
  },
  {
    question: 'What is the difference between Deputyship and an LPA?',
    answer:
      'If no LPA is in place and a person loses mental capacity, an application must be made to the Court of Protection for a Deputyship Order. Deputyship is court-appointed, can take months to obtain, involves ongoing supervision, is significantly more costly and requires annual reporting. An LPA, by contrast, is chosen by you in advance, avoids the need for court proceedings, is more efficient and provides greater flexibility. Putting an LPA in place early prevents the delay and expense of Deputyship.',
  },
  {
    question: 'How long does it take to register a Lasting Power of Attorney?',
    answer:
      'Registration with the Office of the Public Guardian typically takes 8 to 12 weeks after the documents have been signed. It is strongly recommended that LPAs are registered immediately after completion rather than waiting until they are urgently required.',
  },
  {
    question: 'What is the role of the Certificate Provider in a Lasting Power of Attorney?',
    answer:
      'The Certificate Provider is an independent person who signs the LPA confirming that: you understand the nature and purpose of the LPA, you have the mental capacity to make this decision, and you are not acting under pressure, coercion or undue influence.',
  },
  {
    question: 'Who can act as a Certificate Provider?',
    answer:
      'A Certificate Provider must be either someone who has known you personally for two or more years, or a professional such as a solicitor, GP or other qualified person. They must be independent and cannot be: an attorney named in the LPA, a family member of the donor or an attorney, or an employee of a care home where the donor lives.',
  },
  {
    question: 'Can a Lasting Power of Attorney be changed?',
    answer:
      'A Lasting Power of Attorney cannot be amended once it has been signed and registered. However, if you still have mental capacity, you can revoke your existing LPA and create a new one. It is advisable to review your LPA after significant life events such as marriage, divorce, bereavement or changes in your financial circumstances.',
  },
  {
    question: 'Can an attorney be removed from a Lasting Power of Attorney?',
    answer:
      'If you have mental capacity, you can revoke the existing LPA and create a new one with different attorney appointments. If you lack capacity, an application would need to be made to the Court of Protection. Careful drafting at the outset, including the appointment of replacement attorneys, can help prevent issues arising in the future.',
  },
  {
    question: 'When does a Lasting Power of Attorney come into effect?',
    answer:
      'An LPA must first be registered with the Office of the Public Guardian before it can be used. A Property and Financial Affairs LPA can be used as soon as it is registered, provided the donor consents. A Health and Welfare LPA can only be used if the donor lacks the mental capacity to make the relevant decision themselves.',
  },
  {
    question: 'What happens to a Lasting Power of Attorney when someone dies?',
    answer:
      'A Lasting Power of Attorney automatically comes to an end when the donor dies. The attorneys\' authority ceases immediately upon death. The administration of the estate then passes to the executors appointed under the deceased\'s Will.',
  },
  {
    question: 'Who can act as an attorney under an LPA?',
    answer:
      'An attorney must be over 18 years of age, have mental capacity and must not be bankrupt (in the case of a Property and Financial Affairs LPA). Common choices include a spouse or partner, adult children, a trusted friend or a professional adviser such as a solicitor.',
  },
  {
    question: 'How much does a Lasting Power of Attorney cost in England and Wales?',
    answer:
      'The Office of the Public Guardian charges a registration fee of \u00A382 per LPA. If you are putting both types in place, the total government fee is \u00A3164. Professional fees for preparation and advice vary between providers. You should expect transparent costs to be provided before you proceed.',
  },
  {
    question: 'Do I need a solicitor to prepare a Lasting Power of Attorney?',
    answer:
      'A solicitor is not legally required to prepare an LPA, but incorrectly completed applications are frequently rejected by the Office of the Public Guardian. Professional advice ensures technical accuracy, proper structuring of attorney appointments, clear drafting and a reduced risk of delay or rejection. Using a solicitor is strongly recommended where there are property portfolios, business interests or complex family arrangements.',
  },
  {
    question: 'Why arrange a Lasting Power of Attorney now?',
    answer:
      'Loss of mental capacity can arise unexpectedly as a result of illness, dementia, stroke or accident. Without an LPA in place, bank accounts may be frozen, property transactions may be delayed, care decisions may be complicated and an application to the Court of Protection may be required. An LPA provides certainty, continuity and peace of mind for you and your family.',
  },
];

function FAQs() {
  const [openItems, setOpenItems] = useState({});

  const toggleItem = (index) => {
    setOpenItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <div className="faqs-page">
      {/* ── Hero ── */}
      <section className="faqs-hero">
        <div className="faqs-hero__inner">
          <div className="faqs-hero__breadcrumb">
            <Link to="/">Home</Link>
            <span className="faqs-hero__breadcrumb-sep">/</span>
            <span className="faqs-hero__breadcrumb-current">FAQs</span>
          </div>
          <h1 className="faqs-hero__title">Frequently Asked Questions</h1>
          <p className="faqs-hero__subtitle">
            Common questions about Lasting Powers of Attorney in England and Wales
          </p>
        </div>
      </section>

      {/* ── Accordion ── */}
      <section className="faqs-section">
        <div className="faqs-section__inner">
          <div className="faqs-accordion">
            {FAQ_DATA.map((faq, index) => (
              <div
                key={index}
                className={`faqs-accordion__item${openItems[index] ? ' faqs-accordion__item--open' : ''}`}
              >
                <button
                  className="faqs-accordion__trigger"
                  onClick={() => toggleItem(index)}
                  aria-expanded={!!openItems[index]}
                >
                  <span className="faqs-accordion__number">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="faqs-accordion__question">{faq.question}</span>
                  <span className="faqs-accordion__chevron">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M6 9l6 6 6-6"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>
                <div className="faqs-accordion__panel">
                  <div className="faqs-accordion__answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="faqs-cta">
        <div className="faqs-cta__inner">
          <h2 className="faqs-cta__title">Still have questions?</h2>
          <p className="faqs-cta__desc">
            Contact us for personalised advice about your Lasting Power of Attorney
            requirements.
          </p>
          <Link to="/contact" className="btn btn-primary">
            Get in Touch
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginLeft: 8 }}>
              <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </div>
      </section>
    </div>
  );
}

export default FAQs;
